import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, JobIdentity, Spinner, EmptyState } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, PackagePlus, Lock, ShoppingCart, AlertTriangle } from "lucide-react";
import PartPhotos from "@/components/PartPhotos";

const CONDITIONS = ["GOOD", "DAMAGED", "WRONG PART", "SHORT / INCOMPLETE", "OTHER"];

export default function PartMasuk() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const jobId = sp.get("job");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState([]); // per-line receiving state
  const [counts, setCounts] = useState({}); // part_line_id -> initial photo count

  const load = useCallback((silent = false) => {
    if (!jobId) { setLoading(false); return; }
    if (!silent) setLoading(true);
    api.get(`/procurement/pending-receiving?job_id=${jobId}`).then(({ data }) => {
      setData(data);
      setLines(data.parts.map((p) => ({
        part_line_id: p.part_line_id, part_number: p.part_number, part_name: p.part_name,
        ordered_qty: p.ordered_qty, received_qty: p.received_qty, remaining_qty: p.remaining_qty,
        supplier: p.supplier, etd: p.etd, checked: true, qty: p.remaining_qty, condition: "GOOD",
      })));
    }).catch((e) => toast.error(apiError(e, "Gagal tarik data"))).finally(() => setLoading(false));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const upd = (i, k, v) => setLines((ls) => ls.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const setCount = useCallback((plid, n) => setCounts((c) => (c[plid] === n ? c : { ...c, [plid]: n })), []);

  const selected = lines.filter((l) => l.checked);
  const missingPhoto = selected.filter((l) => (counts[l.part_line_id] || 0) < 1);
  const canSubmit = selected.length > 0 && missingPhoto.length === 0 && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        job_id: data.job_id, nopol: data.nopol, claim_number: data.claim_number, spk_number: data.spk_number,
        parts: selected.map((l) => ({ id: l.part_line_id, part_number: l.part_number, part_name: l.part_name, qty: Number(l.qty) || 0, condition: l.condition })),
      };
      const res = await api.post("/receiving/confirm", payload);
      toast.success(`Part diterima • ${res.data.receiving_number}`);
      navigate(`/jobs/${data.job_id}`);
    } catch (err) {
      toast.error(apiError(err, "Gagal terima part"));
    } finally { setSaving(false); }
  };

  if (!jobId) {
    return (
      <div>
        <PageHeader title="Part Datang / Receiving" subtitle="Penerimaan part ditarik dari Part Pesan (Procurement)." />
        <Card className="p-8 text-center">
          <div className="h-16 w-16 rounded-2xl bg-[var(--gold-light)] flex items-center justify-center mx-auto mb-4"><ShoppingCart size={30} className="text-[var(--gold)]" /></div>
          <h3 className="font-head font-semibold text-lg text-gray-900">Mulai dari Part Pesan</h3>
          <p className="text-sm text-gray-500 mt-1 mb-5 max-w-sm mx-auto">Buka claim di <b>Part Pesan</b>, lalu tekan <b>Terima Part</b>. Data part otomatis turun ke sini — tidak perlu input ulang.</p>
          <Button className="bg-gold text-white" onClick={() => navigate("/part-pesan")} data-testid="go-partpesan-btn"><ShoppingCart size={16} className="mr-2" /> Buka Part Pesan</Button>
        </Card>
      </div>
    );
  }

  if (loading) return <div><PageHeader title="Part Datang / Receiving" /><Spinner /></div>;
  if (!data) return <div><PageHeader title="Part Datang / Receiving" /><EmptyState title="Data tidak ditemukan" icon={AlertTriangle} /></div>;

  return (
    <div className="space-y-4">
      <PageHeader title="Part Datang / Receiving" subtitle="Foto kondisi awal (kamera) wajib sebelum part diterima." />

      <Card className="p-4">
        <div className="flex items-start justify-between gap-2">
          <JobIdentity job={data} />
          <Button size="sm" variant="outline" onClick={() => load(true)} data-testid="tarik-data-btn"><RefreshCw size={14} className="mr-1" /> Tarik Data Part Pesan</Button>
        </div>
        <div className="text-xs text-gray-500 mt-2">{data.vehicle_brand} {data.vehicle_type} • {data.insurance}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          <ReadOnly label="Receiving No" value="Otomatis oleh sistem" />
          <ReadOnly label="Tanggal Terima" value="Server date/time (auto)" />
          <ReadOnly label="Diterima Oleh" value="Login user (auto)" />
        </div>
      </Card>

      {lines.length === 0 ? (
        <EmptyState title="Semua part sudah diterima penuh" icon={CheckCircle2} />
      ) : (
        <Card className="p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Belum Diterima ({lines.length})</div>
          <div className="space-y-4">
            {lines.map((l, i) => {
              const photoOk = (counts[l.part_line_id] || 0) >= 1;
              return (
                <div key={l.part_line_id} className="border border-gray-100 rounded-xl p-3" data-testid={`rcv-line-${i}`}>
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={l.checked} onChange={(e) => upd(i, "checked", e.target.checked)} className="mt-1 h-4 w-4" data-testid={`rcv-check-${i}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-data font-semibold text-gray-900">{l.part_number || "—"}</div>
                      <div className="text-sm text-gray-700">{l.part_name}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-500 mt-1">
                        <span>Ordered: <b>{l.ordered_qty}</b></span>
                        <span>Sudah diterima: <b>{l.received_qty}</b></span>
                        <span>Remaining: <b className="text-amber-600">{l.remaining_qty}</b></span>
                        <span className="flex items-center gap-1"><Lock size={11} /> Supplier: <b>{l.supplier || "—"}</b></span>
                        {l.etd && <span>ETD: {String(l.etd).slice(0, 10)}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 pl-7">
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Qty Diterima</label>
                      <Input type="number" min={1} max={l.remaining_qty} value={l.qty} disabled={!l.checked}
                        onChange={(e) => upd(i, "qty", Math.min(Number(e.target.value), l.remaining_qty))}
                        className="mt-1 h-9" data-testid={`rcv-qty-${i}`} />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Condition</label>
                      <select value={l.condition} disabled={!l.checked} onChange={(e) => upd(i, "condition", e.target.value)}
                        className="mt-1 h-9 w-full rounded-md border border-gray-300 bg-transparent px-2 text-sm" data-testid={`rcv-cond-${i}`}>
                        {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3 pl-7">
                    <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1 ${photoOk ? "text-emerald-600" : "text-red-500"}`}>
                      {photoOk ? "✓ Foto Awal OK" : "Foto Kondisi Awal (WAJIB, kamera) *"}
                    </div>
                    <PartPhotos partLineId={l.part_line_id} phase="INITIAL" canUpload compact maxPhotos={5}
                      meta={{ nopol: data.nopol, claim_number: data.claim_number, part_name: l.part_name }}
                      onCount={(n) => setCount(l.part_line_id, n)} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {lines.length > 0 && (
        <div className="sticky bottom-4 flex flex-col sm:flex-row gap-2 sm:justify-end items-stretch sm:items-center bg-white/80 backdrop-blur p-3 rounded-xl border border-gray-100">
          {missingPhoto.length > 0 && (
            <span className="text-xs text-red-500 flex items-center gap-1 sm:mr-auto" data-testid="photo-warning">
              <AlertTriangle size={13} /> Foto kondisi awal wajib sebelum part dapat diterima ({missingPhoto.length} part)
            </span>
          )}
          <Button variant="outline" onClick={() => navigate(`/jobs/${data.job_id}`)}>Batal</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-gray-900" data-testid="terima-part-confirm-btn">
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <PackagePlus size={16} className="mr-2" />} TERIMA PART ({selected.length})
          </Button>
        </div>
      )}
    </div>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1"><Lock size={10} /> {label}</label>
      <div className="mt-1 h-9 flex items-center px-2 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-500">{value}</div>
    </div>
  );
}
